import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { StarButton } from '@/components/StarButton';
import { useAuthor } from '@/hooks/useAuthor';
import { articleSummary, articleTitle, getTagValue, WIKI_ARTICLE_KIND } from '@/lib/nip54';

interface ArticleCardProps {
  event: NostrEvent;
}

export function ArticleCard({ event }: ArticleCardProps) {
  const author = useAuthor(event.pubkey);
  const displayName = author.data?.metadata?.name ?? `${event.pubkey.slice(0, 8)}…`;
  const slug = getTagValue(event, 'd') ?? '';
  const summary = articleSummary(event) ?? event.content.slice(0, 160);
  const npub = nip19.npubEncode(event.pubkey);

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader>
        <CardTitle className="text-lg">
          <Link to={`/w/${encodeURIComponent(slug)}/${npub}`} className="hover:underline">
            {articleTitle(event)}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="line-clamp-3 text-sm text-muted-foreground break-words">{summary}</p>
        <div className="flex items-center justify-between gap-2">
          <Link
            to={`/${npub}`}
            className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <Avatar className="size-5">
              <AvatarImage src={author.data?.metadata?.picture} alt={displayName} />
              <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="truncate">{displayName}</span>
            <span aria-hidden>·</span>
            <time dateTime={new Date(event.created_at * 1000).toISOString()} className="shrink-0">
              {new Date(event.created_at * 1000).toLocaleDateString()}
            </time>
          </Link>
          <StarButton
            event={event}
            addressPointer={`${WIKI_ARTICLE_KIND}:${event.pubkey}:${slug}`}
            className="shrink-0"
          />
        </div>
      </CardContent>
    </Card>
  );
}
